import type {
  Account,
  Arrangement,
  DatePeriod,
  PageGroup,
  StatementView,
} from './types'

// Statements the user starts here have no cycle behind them yet, so they are named apart from the
// ones lukita filed.
const NEW_PREFIX = 'new:'

const ascending = (first: number, second: number) => first - second

const newStatementId = (arrangement: Arrangement) => {
  const highest = arrangement.statements.reduce(
    (top, statement) =>
      statement.id.startsWith(NEW_PREFIX)
        ? Math.max(top, Number(statement.id.slice(NEW_PREFIX.length)))
        : top,
    0,
  )
  return `${NEW_PREFIX}${highest + 1}`
}

export const arrangementFrom = (
  accounts: Account[],
  groups: PageGroup[],
): Arrangement => {
  const statements = groups.map((group) => ({
    id: group.id,
    period: group.period,
    accountId: group.accountId,
  }))
  const ownerByPage = new Map<number, string>()
  for (const group of groups) {
    for (const page of group.pages) ownerByPage.set(page, group.id)
  }
  return { accounts, statements, ownerByPage }
}

export const assignPages = (
  arrangement: Arrangement,
  pages: number[],
  statementId: string,
): Arrangement => {
  const ownerByPage = new Map(arrangement.ownerByPage)
  for (const page of pages) ownerByPage.set(page, statementId)
  const emptied = emptiedByTheMove(arrangement.ownerByPage, ownerByPage)

  return {
    ...arrangement,
    statements: arrangement.statements.filter(
      (statement) => !emptied(statement.id),
    ),
    ownerByPage,
  }
}

// A statement whose last page just moved out was a bad cut, so it goes with the move. One
// the user started on an empty month never held a page, so it stays and waits for some.
const emptiedByTheMove = (
  before: ReadonlyMap<number, string>,
  after: ReadonlyMap<number, string>,
) => {
  const heldBefore = new Set(before.values())
  const heldAfter = new Set(after.values())
  return (statementId: string) =>
    heldBefore.has(statementId) && !heldAfter.has(statementId)
}

export const createStatementFrom = (
  arrangement: Arrangement,
  pages: number[],
  period: DatePeriod | null,
  accountId: string | null,
  id = newStatementId(arrangement),
): Arrangement => {
  return assignPages(
    {
      ...arrangement,
      statements: [...arrangement.statements, { id, period, accountId }],
    },
    pages,
    id,
  )
}

const pagesByStatement = (arrangement: Arrangement) => {
  const pages = new Map<string, number[]>()
  for (const [page, statementId] of arrangement.ownerByPage) {
    const held = pages.get(statementId)
    if (held) {
      held.push(page)
      continue
    }
    pages.set(statementId, [page])
  }
  for (const held of pages.values()) held.sort(ascending)
  return pages
}

const firstPage = (view: StatementView) =>
  view.pages[0] ?? Number.MAX_SAFE_INTEGER

export const inDateOrder = (one: StatementView, other: StatementView) => {
  const onePeriod = one.statement.period
  const otherPeriod = other.statement.period
  if (onePeriod && otherPeriod) {
    if (onePeriod.start !== otherPeriod.start) {
      return onePeriod.start < otherPeriod.start ? -1 : 1
    }
    return firstPage(one) - firstPage(other)
  }
  if (onePeriod) return -1
  if (otherPeriod) return 1
  return firstPage(one) - firstPage(other)
}

// Statements read in date order, which is how the file should look once it is right.
// Anything with no dates yet sits at the end, where it reads as work still to do.
export const statementViews = (arrangement: Arrangement): StatementView[] => {
  const pages = pagesByStatement(arrangement)

  return arrangement.statements
    .map((statement) => ({
      statement,
      pages: pages.get(statement.id) ?? [],
    }))
    .sort(inDateOrder)
}
